// src/domains/dev/SeedPage.jsx — Ambiente completo de testes (Super Admin only).
// Usa apenas serviços já inicializados em firebase.js — sem dynamic imports.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import {
  authService,
  usersService,
  establishmentsService,
  proceduresService,
  scheduleWindowsService,
} from 'infrastructure/firebase';

const T = {
  brand:'#6B5876', border:'#E8E5EC', s2:'#FAF7FB',
  text:'#3D3447', muted:'#A8A1B0',
  sans:"'DM Sans',sans-serif", serif:"'Instrument Serif',serif",
};

const PASSWORD = '123456';

// ─── Definição do ambiente ────────────────────────────────────────────────────
const CLINICAS = [
  {
    key: 'vida', icon: '🏥',
    tipo: 'Clínica Médica',
    nome: 'Clínica Vida+',
    categoria: 'medicina',
    cnpj: '11111111000111',
    adminEmail: 'admin.vidamais@teste.com',
    adminName: 'Admin Vida+',
    profissionais: [
      { nome:'Dr. Carlos Silva',    email:'carlos.silva@teste.com',    specialty:'Clínica Geral', avatarColor:'blue'     },
      { nome:'Dra. Mariana Souza',  email:'mariana.souza@teste.com',   specialty:'Cardiologia',   avatarColor:'rose'     },
    ],
    procedimentos: [
      { name:'Consulta Clínica Geral', duration:30, price:180, emoji:'🩺', category:'Outros'    },
      { name:'Retorno Médico',         duration:20, price:120, emoji:'📋', category:'Outros'    },
      { name:'Avaliação Médica',       duration:45, price:220, emoji:'❤️', category:'Outros'    },
    ],
  },
  {
    key: 'odonto', icon: '🦷',
    tipo: 'Clínica Odontológica',
    nome: 'Odonto Prime',
    categoria: 'odontologia',
    cnpj: '22222222000122',
    adminEmail: 'admin.odontoprime@teste.com',
    adminName: 'Admin Odonto Prime',
    profissionais: [
      { nome:'Dr. Rafael Lima',      email:'rafael.lima@teste.com',     specialty:'Odontologia',  avatarColor:'mint'     },
      { nome:'Dra. Fernanda Costa',  email:'fernanda.costa@teste.com',  specialty:'Ortodontia',   avatarColor:'lavender' },
    ],
    procedimentos: [
      { name:'Limpeza Dental',          duration:45, price:150, emoji:'🦷', category:'Outros' },
      { name:'Avaliação Odontológica',  duration:30, price:100, emoji:'🔍', category:'Outros' },
      { name:'Clareamento Dental',      duration:60, price:350, emoji:'✨', category:'Outros' },
    ],
  },
  {
    key: 'studio', icon: '💄',
    tipo: 'Salão de Beleza',
    nome: 'Studio Elegance',
    categoria: 'salao',
    cnpj: '33333333000133',
    adminEmail: 'admin.studio@teste.com',
    adminName: 'Admin Studio',
    profissionais: [
      { nome:'Juliana Martins', email:'juliana.martins@teste.com', specialty:'Cabeleireira', avatarColor:'peach' },
      { nome:'Patrícia Rocha',  email:'patricia.rocha@teste.com',  specialty:'Coloração',    avatarColor:'rose'  },
    ],
    procedimentos: [
      { name:'Corte Feminino', duration:60,  price:80,  emoji:'✂️',  category:'Capilar' },
      { name:'Escova',         duration:45,  price:60,  emoji:'💨',  category:'Capilar' },
      { name:'Hidratação',     duration:60,  price:90,  emoji:'💧',  category:'Capilar' },
      { name:'Coloração',      duration:120, price:180, emoji:'🎨',  category:'Capilar' },
    ],
  },
];

const AUTONOMOS = [
  {
    key:'lucas', icon:'🦴',
    nome:'Lucas Almeida', email:'lucas.almeida@teste.com',
    specialty:'Fisioterapia', avatarColor:'blue',
    procedimentos: [
      { name:'Sessão de Fisioterapia', duration:60, price:120, emoji:'🦴', category:'Corporais' },
      { name:'Avaliação Postural',     duration:45, price:150, emoji:'📐', category:'Corporais' },
    ],
  },
  {
    key:'ana', icon:'🧠',
    nome:'Ana Ribeiro', email:'ana.ribeiro@teste.com',
    specialty:'Psicologia', avatarColor:'lavender',
    procedimentos: [
      { name:'Sessão de Psicoterapia', duration:50, price:200, emoji:'🧠', category:'Outros' },
      { name:'Avaliação Psicológica',  duration:60, price:250, emoji:'📝', category:'Outros' },
    ],
  },
];

// Clientes de teste — necessários para que admins e profissionais possam criar agendamentos
const CLIENTES_TESTE = [
  { key:'cli1', nome:'Maria Santos',   email:'maria.santos@teste.com',   phone:'(11) 99001-0001', avatarColor:'rose'     },
  { key:'cli2', nome:'João Ferreira',  email:'joao.ferreira@teste.com',  phone:'(11) 99001-0002', avatarColor:'blue'     },
  { key:'cli3', nome:'Carla Mendes',   email:'carla.mendes@teste.com',   phone:'(11) 99001-0003', avatarColor:'lavender' },
];

// ─── Limpa janelas de bloqueio de um profissional ──────────────────────────────
// janelaAgenda no sistema = BLOQUEIO de horário (não disponibilidade).
// O seed anterior criava janelas 08:00–18:00 por engano, bloqueando todo o dia.
// Esta função remove todas essas janelas para que os horários fiquem disponíveis.
async function limparJanelas(professionalId) {
  try {
    const n = await scheduleWindowsService.deleteByProfessional(professionalId);
    return n;
  } catch { return 0; }
}

// ─── Coleta todos os UIDs dos profissionais de teste ─────────────────────────
async function coletarUidsProfissionais() {
  const uids = [];
  for (const c of CLINICAS) {
    for (const p of c.profissionais) {
      const u = await usersService.getByEmail(p.email);
      if (u) uids.push(u.id);
    }
  }
  for (const a of AUTONOMOS) {
    const u = await usersService.getByEmail(a.email);
    if (u) uids.push(u.id);
  }
  return uids;
}

// ─── StatusDot ────────────────────────────────────────────────────────────────
function StatusDot({ status }) {
  const C = { ok:'#166534', skip:'#1e40af', error:'#9a3412', pending:'#A8A1B0', running:'#854D0E' };
  const L = { ok:'Criado', skip:'Já existe', error:'Erro', pending:'—', running:'...' };
  const c = C[status] ?? C.pending;
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:11, color:c, fontWeight:600 }}>
      <span style={{ width:6, height:6, borderRadius:'50%', background:c }} />
      {L[status] ?? '—'}
    </span>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function SeedPage() {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [running, setRunning] = useState(false);
  const [res, setRes]         = useState({});
  const [logs, setLogs]       = useState([]);
  const [done, setDone]       = useState(false);

  if (!isSuperAdmin) {
    return (
      <div style={{ padding:40, fontFamily:T.sans, color:T.muted, textAlign:'center' }}>
        Acesso restrito ao Super Admin.
      </div>
    );
  }

  const log  = (msg) => setLogs(p => [...p, msg]);
  const setS = (k, s) => setRes(p => ({ ...p, [k]: s }));

  // ── Seed de uma clínica ──────────────────────────────────────────────────
  const seedClinica = async (c) => {
    log(`\n${c.icon} ${c.nome} (${c.tipo})`);

    // 1. Admin
    let adminUid = null;
    let estabId  = null;
    const existAdmin = await usersService.getByEmail(c.adminEmail);
    if (existAdmin) {
      adminUid = existAdmin.id;
      estabId  = existAdmin.estabelecimentoId || null;
      setS(`${c.key}_admin`, 'skip');
      log(`  → Admin já existe.`);
    } else {
      setS(`${c.key}_admin`, 'running');
      const cred = await authService.register({
        name: c.adminName, email: c.adminEmail, password: PASSWORD,
        role: 'clinicAdmin', phone: '(11) 90000-0000',
        subscriptionStatus: 'ACTIVE', accessExpiresAt: null,
      });
      adminUid = cred.uid;
      setS(`${c.key}_admin`, 'ok');
      log(`  ✔ Admin criado.`);
    }

    // 2. Estabelecimento
    if (!estabId) {
      const existEstab = await establishmentsService.getByNomeFantasia(c.nome);
      if (existEstab) {
        estabId = existEstab.id;
        setS(`${c.key}_estab`, 'skip');
        log(`  → Estabelecimento já existe.`);
      } else {
        setS(`${c.key}_estab`, 'running');
        const ref = await establishmentsService.add({
          nomeFantasia: c.nome, razaoSocial: `${c.nome} Ltda.`,
          cnpj: c.cnpj, email: c.adminEmail,
          telefone: '(11) 90000-0000', categoria: c.categoria,
          planoId: null, foto: null, banner: null, descricao: '',
          adminUid,
        });
        estabId = ref.id;
        setS(`${c.key}_estab`, 'ok');
        log(`  ✔ Estabelecimento criado.`);
      }
    }

    // Garante vínculo admin ↔ estab
    if (adminUid && estabId) {
      await usersService.update(adminUid, {
        estabelecimentoId: estabId,
        subscriptionStatus: 'ACTIVE', accessExpiresAt: null,
      });
    }

    // 3. Profissionais
    const proUids = [];
    for (const p of c.profissionais) {
      const exist = await usersService.getByEmail(p.email);
      if (exist) {
        proUids.push(exist.id);
        setS(`${c.key}_${p.email}`, 'skip');
        log(`  → ${p.nome}: já existe.`);
      } else {
        setS(`${c.key}_${p.email}`, 'running');
        const cred = await authService.register({
          name: p.nome, email: p.email, password: PASSWORD,
          role: 'professional', phone: '(11) 90000-0001',
          specialty: p.specialty, estabelecimentoId: estabId,
          subscriptionStatus: 'ACTIVE', accessExpiresAt: null,
        });
        await usersService.update(cred.uid, { avatarColor: p.avatarColor });
        proUids.push(cred.uid);
        setS(`${c.key}_${p.email}`, 'ok');
        log(`  ✔ ${p.nome} criado.`);
      }
    }

    // 4. Procedimentos coerentes com a especialidade
    for (const proc of c.procedimentos) {
      const exist = await proceduresService.getByName(proc.name);
      if (exist) {
        setS(`${c.key}_proc_${proc.name}`, 'skip');
        log(`  → "${proc.name}": já existe.`);
      } else {
        setS(`${c.key}_proc_${proc.name}`, 'running');
        const ref = await proceduresService.add({
          ...proc,
          professionals: proUids, professionalId: proUids[0] || null,
          estabelecimentoId: estabId, approved: true, description: '',
        });
        await proceduresService.approve(ref.id);
        setS(`${c.key}_proc_${proc.name}`, 'ok');
        log(`  ✔ "${proc.name}" criado e aprovado.`);
      }
    }

    // 5. Limpa janelas de bloqueio antigas (criadas por versões anteriores do seed)
    //    janelaAgenda = BLOQUEIO; sem janelas = todos horários do businessConfig disponíveis.
    for (let i = 0; i < proUids.length; i++) {
      const uid  = proUids[i];
      const nome = c.profissionais[i]?.nome || 'Profissional';
      setS(`${c.key}_win_${uid}`, 'running');
      const n = await limparJanelas(uid);
      setS(`${c.key}_win_${uid}`, 'ok');
      if (n > 0) log(`  ✔ ${n} janela(s) de bloqueio removida(s) de ${nome}.`);
      else log(`  → Nenhuma janela de bloqueio em ${nome}.`);
    }

    log(`  ✅ ${c.nome} configurada!`);
  };

  // ── Seed de profissional autônomo ────────────────────────────────────────
  const seedAutonomo = async (a) => {
    log(`\n${a.icon} ${a.nome} (Autônomo — ${a.specialty})`);

    let uid = null;
    const exist = await usersService.getByEmail(a.email);
    if (exist) {
      uid = exist.id;
      setS(a.key, 'skip');
      log(`  → Já existe.`);
    } else {
      setS(a.key, 'running');
      const cred = await authService.register({
        name: a.nome, email: a.email, password: PASSWORD,
        role: 'professional', phone: '(11) 90000-0009',
        specialty: a.specialty,
        subscriptionStatus: 'ACTIVE', accessExpiresAt: null,
      });
      await usersService.update(cred.uid, { avatarColor: a.avatarColor });
      uid = cred.uid;
      setS(a.key, 'ok');
      log(`  ✔ ${a.nome} criado.`);
    }

    for (const proc of a.procedimentos) {
      const exist = await proceduresService.getByName(proc.name);
      if (exist) {
        setS(`${a.key}_proc_${proc.name}`, 'skip');
        log(`  → "${proc.name}": já existe.`);
      } else {
        setS(`${a.key}_proc_${proc.name}`, 'running');
        const ref = await proceduresService.add({
          ...proc,
          professionals: [uid], professionalId: uid,
          estabelecimentoId: null, approved: true, description: '',
        });
        await proceduresService.approve(ref.id);
        setS(`${a.key}_proc_${proc.name}`, 'ok');
        log(`  ✔ "${proc.name}" criado e aprovado.`);
      }
    }

    // Limpa janelas de bloqueio antigas do autônomo
    setS(`${a.key}_win`, 'running');
    const n = await limparJanelas(uid);
    setS(`${a.key}_win`, 'ok');
    if (n > 0) log(`  ✔ ${n} janela(s) de bloqueio removida(s).`);
    else log(`  → Nenhuma janela de bloqueio.`);
    log(`  ✅ ${a.nome} configurado!`);
  };

  // ── Seed de clientes de teste ────────────────────────────────────────────
  const seedClientes = async () => {
    log('\n👥 Clientes de teste');
    for (const c of CLIENTES_TESTE) {
      const exist = await usersService.getByEmail(c.email);
      if (exist) {
        setS(c.key, 'skip');
        log(`  → ${c.nome}: já existe.`);
      } else {
        setS(c.key, 'running');
        const cred = await authService.register({
          name: c.nome, email: c.email, password: PASSWORD,
          role: 'client', phone: c.phone,
        });
        await usersService.update(cred.uid, { avatarColor: c.avatarColor });
        setS(c.key, 'ok');
        log(`  ✔ ${c.nome} criado.`);
      }
    }
  };

  // ── Limpeza independente de janelas de bloqueio ──────────────────────────
  const limparTodasJanelas = async () => {
    setRunning(true);
    setLogs([]);
    log('🧹 Limpando janelas de bloqueio dos profissionais de teste...');
    try {
      const uids = await coletarUidsProfissionais();
      if (uids.length === 0) {
        log('  ⚠ Nenhum profissional de teste encontrado. Execute o seed primeiro.');
      } else {
        let total = 0;
        for (const uid of uids) {
          const n = await limparJanelas(uid);
          total += n;
        }
        log(`\n✅ ${total} janela(s) removida(s) de ${uids.length} profissional(is).`);
        log('Horários agora livres para agendamento.');
      }
    } catch (err) {
      log(`\n❌ Erro: ${err?.code ?? err?.message ?? String(err)}`);
    } finally {
      setRunning(false);
    }
  };

  // ── Executar tudo ────────────────────────────────────────────────────────
  const runSeed = async () => {
    setRunning(true);
    setLogs([]);
    setRes({});
    setDone(false);
    log('🚀 Iniciando seed...');
    try {
      for (const c of CLINICAS)  await seedClinica(c);
      for (const a of AUTONOMOS) await seedAutonomo(a);
      await seedClientes();
      log('\n🎉 Seed concluído! Senha de todos os usuários: 123456');
      setDone(true);
    } catch (err) {
      log(`\n❌ Erro: ${err?.code ?? err?.message ?? String(err)}`);
    } finally {
      setRunning(false);
    }
  };

  // ── Migração de role: admin (legado) → clinicAdmin ─────────────────────────
  // Roda como super admin (regras permitem super admin alterar role). Converte
  // todos os docs `usuarios` com role legado 'admin' para 'clinicAdmin'. Idempotente.
  const migrarRolesAdmin = async () => {
    setRunning(true);
    setLogs([]);
    setDone(false);
    log('🔁 Migrando role "admin" (legado) → "clinicAdmin"...');
    try {
      const legados = await usersService.getByRole('admin');
      if (legados.length === 0) {
        log('Nenhum doc com role "admin" encontrado. Nada a migrar.');
      } else {
        for (const u of legados) {
          await usersService.update(u.id, { role: 'clinicAdmin' });
          log(`  ✔ ${u.email || u.id} → clinicAdmin`);
        }
        log(`\n🎉 ${legados.length} usuário(s) migrado(s) para clinicAdmin.`);
      }
      setDone(true);
    } catch (err) {
      log(`\n❌ Erro: ${err?.code ?? err?.message ?? String(err)}`);
    } finally {
      setRunning(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  const credenciais = [
    ...CLINICAS.map(c => ({ label:`${c.nome} (Admin)`, email:c.adminEmail, role:'clinicAdmin' })),
    ...CLINICAS.flatMap(c => c.profissionais.map(p => ({ label:p.nome, email:p.email, role:'pro' }))),
    ...AUTONOMOS.map(a => ({ label:`${a.nome} (Autônomo)`, email:a.email, role:'pro' })),
    ...CLIENTES_TESTE.map(c => ({ label:`${c.nome} (Cliente)`, email:c.email, role:'client' })),
  ];

  return (
    <div style={{ fontFamily:T.sans }}>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontFamily:T.serif, fontSize:32, color:T.text }}>Ambiente de Testes</div>
        <div style={{ fontSize:13, color:T.muted, marginTop:2 }}>
          3 clínicas + 2 autônomos · procedimentos aprovados · horários livres (sem bloqueios) · idempotente.
        </div>
      </div>

      {/* Cards das clínicas */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:14, marginBottom:20 }}>
        {CLINICAS.map(c => (
          <div key={c.key} style={{ background:'#fff', border:`1px solid ${T.border}`, borderRadius:14, padding:'16px 18px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
              <span style={{ fontSize:22 }}>{c.icon}</span>
              <div>
                <div style={{ fontWeight:600, color:T.text, fontSize:14 }}>{c.nome}</div>
                <div style={{ fontSize:11, color:T.muted }}>{c.tipo}</div>
              </div>
              <div style={{ marginLeft:'auto' }}>
                <StatusDot status={res[`${c.key}_estab`] ?? 'pending'} />
              </div>
            </div>
            {c.profissionais.map(p => (
              <div key={p.email} style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#6B6070', marginBottom:3 }}>
                <span>👤 {p.nome}</span>
                <StatusDot status={res[`${c.key}_${p.email}`] ?? 'pending'} />
              </div>
            ))}
            <div style={{ borderTop:`1px solid ${T.border}`, marginTop:8, paddingTop:8 }}>
              {c.procedimentos.map(p => (
                <div key={p.name} style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:T.muted, marginBottom:2 }}>
                  <span>{p.emoji} {p.name}</span>
                  <StatusDot status={res[`${c.key}_proc_${p.name}`] ?? 'pending'} />
                </div>
              ))}
            </div>
          </div>
        ))}
        {AUTONOMOS.map(a => (
          <div key={a.key} style={{ background:'#fff', border:`1px solid ${T.border}`, borderRadius:14, padding:'16px 18px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
              <span style={{ fontSize:22 }}>{a.icon}</span>
              <div>
                <div style={{ fontWeight:600, color:T.text, fontSize:14 }}>{a.nome}</div>
                <div style={{ fontSize:11, color:T.muted }}>Autônomo — {a.specialty}</div>
              </div>
              <div style={{ marginLeft:'auto' }}>
                <StatusDot status={res[a.key] ?? 'pending'} />
              </div>
            </div>
            {a.procedimentos.map(p => (
              <div key={p.name} style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:T.muted, marginBottom:2 }}>
                <span>{p.emoji} {p.name}</span>
                <StatusDot status={res[`${a.key}_proc_${p.name}`] ?? 'pending'} />
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Aviso sobre janelas de bloqueio */}
      <div style={{ background:'#fffbeb', border:'1px solid #fbbf24', borderRadius:12, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#92400e' }}>
        <strong>⚠ Se não houver horários disponíveis no calendário:</strong> clique em "Limpar Janelas de Bloqueio" para remover bloqueios criados por versões anteriores do seed.
      </div>

      {/* Credenciais */}
      <div style={{ background:'#fff', border:`1px solid ${T.border}`, borderRadius:14, padding:'16px 20px', marginBottom:20 }}>
        <div style={{ fontSize:11, fontWeight:600, color:T.muted, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>
          Credenciais · senha: <code style={{ background:'#F0EDF3', padding:'1px 6px', borderRadius:4, fontSize:12 }}>123456</code>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:6 }}>
          {credenciais.map(u => {
            const roleColor = { admin:'#E6DEF0', pro:'#E7F4ED', client:'#DDE5F0' }[u.role] || T.s2;
            const roleLabel = { admin:'Admin', pro:'Profissional', client:'Cliente' }[u.role] || '';
            return (
              <div key={u.email} style={{ fontSize:12, padding:'8px 12px', background:roleColor, borderRadius:8 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:2 }}>
                  <div style={{ fontWeight:600, color:T.text }}>{u.label}</div>
                  <span style={{ fontSize:10, padding:'1px 7px', borderRadius:20, background:'rgba(0,0,0,0.06)', color:'#555' }}>{roleLabel}</span>
                </div>
                <div style={{ color:T.muted, fontFamily:'monospace', fontSize:11 }}>{u.email}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Terminal de log */}
      {logs.length > 0 && (
        <div style={{ background:'#1a1a2e', borderRadius:12, padding:'14px 18px', marginBottom:20, maxHeight:320, overflowY:'auto' }}>
          {logs.map((l, i) => (
            <div key={i} style={{
              fontSize:12, fontFamily:'monospace', lineHeight:1.7, whiteSpace:'pre-wrap',
              color: l.includes('❌') ? '#fca5a5'
                   : l.includes('🎉') || l.includes('✅') ? '#86efac'
                   : l.includes('✔') ? '#6ee7b7'
                   : '#a8d8ea',
            }}>{l}</div>
          ))}
        </div>
      )}

      {/* Ações */}
      <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'center' }}>
        <button disabled={running} onClick={runSeed}
          style={{ padding:'12px 28px', background: running ? T.muted : T.brand, color:'#fff', border:'none', borderRadius:12, fontSize:14, fontWeight:600, cursor: running ? 'not-allowed' : 'pointer', fontFamily:T.sans, opacity: running ? 0.8 : 1 }}>
          {running ? '⏳ Executando...' : done ? '↺ Executar novamente' : '▶ Executar Seed Completo'}
        </button>

        {/* Botão de limpeza independente — resolve bloqueios sem recriar dados */}
        <button disabled={running} onClick={limparTodasJanelas}
          style={{ padding:'12px 20px', background: running ? T.muted : '#fff', color: running ? '#fff' : '#854D0E', border:'1.5px solid #fbbf24', borderRadius:12, fontSize:13, fontWeight:500, cursor: running ? 'not-allowed' : 'pointer', fontFamily:T.sans, opacity: running ? 0.7 : 1 }}>
          🧹 Limpar Janelas de Bloqueio
        </button>

        {/* Migração one-click do role legado 'admin' → 'clinicAdmin' */}
        <button disabled={running} onClick={migrarRolesAdmin}
          style={{ padding:'12px 20px', background: running ? T.muted : '#fff', color: running ? '#fff' : '#5A4E66', border:`1.5px solid ${T.brand}`, borderRadius:12, fontSize:13, fontWeight:500, cursor: running ? 'not-allowed' : 'pointer', fontFamily:T.sans, opacity: running ? 0.7 : 1 }}>
          🔁 Migrar admin → clinicAdmin
        </button>

        {done && (
          <>
            <button onClick={() => navigate('/admin/users')}
              style={{ padding:'12px 22px', background:'#F3F0F7', border:'none', borderRadius:12, fontSize:14, color:'#6B6070', cursor:'pointer', fontFamily:T.sans }}>
              Ver usuários →
            </button>
            <button onClick={() => navigate('/admin/procedures')}
              style={{ padding:'12px 22px', background:'#F3F0F7', border:'none', borderRadius:12, fontSize:14, color:'#6B6070', cursor:'pointer', fontFamily:T.sans }}>
              Ver procedimentos →
            </button>
          </>
        )}
      </div>

      {done && (
        <div style={{ marginTop:20, padding:'14px 18px', background:'#f0fdf4', border:'1px solid #86efac', borderRadius:12, fontSize:13, color:'#166534' }}>
          <strong>Pronto!</strong> Acesse com qualquer e-mail acima, senha <code style={{ fontFamily:'monospace', background:'#dcfce7', padding:'1px 5px', borderRadius:4 }}>123456</code>.
          Horários livres seg–sáb 08:00–18:00. Se o calendário aparecer sem slots, clique em "Limpar Janelas de Bloqueio".
        </div>
      )}
    </div>
  );
}
